import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MoreHorizontal, Plus, Settings, SmilePlus } from 'lucide-react';

import { RECO_SHARE_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { useAuthStore } from '@/stores/AuthState';
import { useQueryClient } from '@tanstack/react-query';

import { useGroups, GROUPS_QUERY_KEY } from '@/hooks/queries/groups';
import { markGroupSeen } from '@/api/groups';
import { useDiscoverFeed } from '@/hooks/queries/discover';
import { useFeedFilter } from '@/stores/feedFilter';
import StyleFilterBar, { StyleFilterClearButton } from '@/components/StyleFilterBar';
import StyleFilterTrigger from '@/components/StyleFilterTrigger';
import FilterHiddenAllState from '@/components/FilterHiddenAllState';
import { getTrackImageUrl } from '@/utils/imageHelpers';
import { shouldShowPendingDot, unseenBadgeLabel } from '@/utils/groups';
import type { GroupMembershipMeta } from '@/types/group';
import { getPreviewUrl, useAudioPlayer } from '@/stores/audioPlayer';
import { useUiSignals } from '@/stores/uiSignals';
import { useOnNewSignal } from '@/hooks/useOnNewSignal';
import { useOnHide } from '@/hooks/useOnHide';
import { landingStep } from '@/utils/deckLanding';
import { captionSheetFor } from '@/utils/captionGesture';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import PullToRefreshSpinner from '@/components/PullToRefreshSpinner';
import { useRecoDraft } from '@/stores/recoDraft';
import { useReactions } from '@/hooks/useReactions';
import { reactionUserEntries } from '@/utils/reactions';
import { pickCaption, pickCommentDisplay, syncCaption } from '@/utils/commentDisplay';
import { updateTrackWithDeezer } from '@/api/track';
import { trackRecommendationViewing, trackTrackOpening } from '@/api/tracking';
import { mapDiscoverTrackToTrack } from '@/services/racoreMapper';
import { ButtonSystem } from '@/components/Buttons';
import ServiceLogo, { getServiceOpenUrl } from '@/components/ServiceLogo';
import { openServiceUrl } from '@/utils/serviceOpen';
import DiscoverCardMenu from '@/components/DiscoverCardMenu';
import DiscussionCount from '@/components/DiscussionCount';
import TrackPanel from '@/components/TrackPanel';
import RoundPlayButton from '@/components/RoundPlayButton';
import { useCircleThread } from '@/hooks/useCircleThread';
import ReactionPicker from '@/components/ReactionPicker';
import ReactionsSheet from '@/components/ReactionsSheet';
import CaptionSheet from '@/components/CaptionSheet';
import DiscussionSheet from '@/components/DiscussionSheet';
import FloatingReactions from '@/components/FloatingReactions';
import CreateCircleSheet from '@/components/CreateCircleSheet';
import JoinCircleSheet from '@/components/JoinCircleSheet';
import ManageCircleScreen from '@/components/ManageCircleScreen';
import BottomSheet from '@/components/BottomSheet';
import { RacoreText } from '@/components/Logos';
import EnvLabel from '@/components/EnvLabel';
import { cn } from '@/lib/utils';
import InviteLinkSheet from '@/components/InviteLinkSheet';
import type { DiscoverResponse, Group } from '@/types';

/**
 * Home / Discover (wireframe 3). Header + circle selector + vertical
 * scroll-snap deck of recommendations (public feed until Groups ships).
 */
export const Route = createFileRoute('/_tabs/home')({
  component: HomeScreen,
});

const RACORE_OFFICIAL_SLUG = 'racore-official';
const PUBLIC_CIRCLE_ID = -1;

/**
 * F4 — how long a card must stay snapped before it claims the audio player.
 * Long enough to swallow the cards a fast flick sweeps past, short enough that
 * a deliberate swipe still feels instant.
 */
const AUTOPLAY_SETTLE_MS = 350;

interface CircleVM {
  id: number;
  name: string;
  emoji: string | null;
  slug: string;
  membership?: GroupMembershipMeta | null;
}

function HomeScreen() {
  const { t } = useTranslation();
  const { groups } = useGroups();
  const deckRef = useRef<HTMLDivElement>(null);
  const scrollSignal = useUiSignals((s) => s.homeScrollTop);

  const racoreOfficial = useMemo(
    () => groups.find((g) => g.slug === RACORE_OFFICIAL_SLUG) ?? null,
    [groups],
  );

  const circles = useMemo<CircleVM[]>(() => {
    if (groups.length > 0) {
      const official = racoreOfficial ? [racoreOfficial] : [];
      const rest = groups.filter((g) => g.id !== racoreOfficial?.id && g.isActive !== false);
      return [...official, ...rest].map((g) => ({
        id: g.id,
        name: g.name,
        emoji: g.emoji,
        slug: g.slug,
        membership: g._membership ?? null,
      }));
    }
    return [{ id: PUBLIC_CIRCLE_ID, name: 'Racore', emoji: '🌍', slug: RACORE_OFFICIAL_SLUG }];
  }, [groups, racoreOfficial]);

  const defaultCircleId = racoreOfficial?.id ?? PUBLIC_CIRCLE_ID;
  const [picked, setPicked] = useState<number | null>(null);
  const [newSheet, setNewSheet] = useState<'choose' | 'create' | 'join' | null>(null);
  const [manageId, setManageId] = useState<number | null>(null);
  // D2 — right after creating a circle, propose sharing its invite link.
  const [shareGroup, setShareGroup] = useState<Group | null>(null);
  const activeCircleId = picked ?? defaultCircleId;
  const activeCircle = circles.find((c) => c.id === activeCircleId) ?? null;
  const isRacoreCircle =
    activeCircleId === PUBLIC_CIRCLE_ID || activeCircleId === racoreOfficial?.id;

  const feedParams = useMemo(() => {
    if (activeCircleId === PUBLIC_CIRCLE_ID) return { selectedGroupId: null, includePublic: false };
    if (racoreOfficial && activeCircleId === racoreOfficial.id) {
      return { selectedGroupId: activeCircleId, includePublic: true };
    }
    return { selectedGroupId: activeCircleId, includePublic: false };
  }, [activeCircleId, racoreOfficial]);

  // feed-genre-filter — ONE global selection, applied instantly. `commit()`
  // (which pushes it to the account) fires when the filter surface closes,
  // never on a toggle: one PUT per filtering session.
  const styles = useFeedFilter((s) => s.styles);
  const toggleStyle = useFeedFilter((s) => s.toggle);
  const clearStyles = useFeedFilter((s) => s.clear);
  const commitStyles = useFeedFilter((s) => s.commit);
  const [filterOpen, setFilterOpen] = useState(false);

  /**
   * feed-genre-filter — closing the sheet IS the decision signal (α): the
   * selection is pushed once, here, and never on a toggle.
   */
  const closeFilterSheet = () => {
    setFilterOpen(false);
    commitStyles();
  };

  const { data, isFetching, isError, fetchNextPage, hasNextPage, refetch } = useDiscoverFeed({
    ...feedParams,
    styles,
  });
  const { refetch: refetchGroups } = useGroups();
  const feed = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const isLoadingFeed = isFetching && feed.length === 0;
  // Sent only once the backend contract ships; `undefined` renders nothing —
  // never a "0" the server did not say.
  const hiddenCount = data?.pages[0]?.meta?.hiddenCount;
  const hiddenAll = feed.length === 0 && typeof hiddenCount === 'number' && hiddenCount > 0;

  // D1 — pull-to-refresh, shared with My Recos and Profile so the gesture feels
  // identical everywhere. Refreshing the feed also refreshes the circle list, so
  // a role change (just been promoted to admin) shows up without leaving Home.
  const { pullY, refreshing, handlers } = usePullToRefresh(() =>
    Promise.all([refetch(), refetchGroups()]),
  );

  // If the selected circle disappears (deleted / left), fall back to default.
  useEffect(() => {
    if (picked != null && !groups.some((g) => g.id === picked)) setPicked(null);
  }, [groups, picked]);

  // A9 — opening a circle clears its unseen badge (server-side baseline reset).
  const queryClient = useQueryClient();
  // This effect must keep `circles` as a dep (the badge state it reads arrives
  // asynchronously), but its own success invalidates the groups query — which
  // hands it a new `circles` reference and re-runs it. Without this guard that
  // is a POST-per-refetch loop whenever the badge doesn't drop to 0 right away
  // (slow server, or a failed request on a flaky link). One POST per circle per
  // mount is enough: the baseline is server-side, so a fresh mount re-arms it.
  const seenSentRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (activeCircleId === PUBLIC_CIRCLE_ID) return;
    if (seenSentRef.current.has(activeCircleId)) return;
    const circle = circles.find((c) => c.id === activeCircleId);
    if (!circle || unseenBadgeLabel(circle.membership) == null) return;
    seenSentRef.current.add(activeCircleId);
    void markGroupSeen(activeCircleId)
      .then(() => queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY }))
      // Badge freshness is best-effort — never surface an error, but do re-arm
      // so a later visit can retry instead of leaving the badge stuck.
      .catch(() => seenSentRef.current.delete(activeCircleId));
  }, [activeCircleId, circles, queryClient]);

  // After sharing a reco, land on the circle it was shared with (so it's visible).
  const landOnCircleId = useUiSignals((s) => s.landOnCircleId);
  useEffect(() => {
    if (landOnCircleId != null) {
      setPicked(landOnCircleId);
      useUiSignals.getState().setLandOnCircle(null);
    }
  }, [landOnCircleId]);

  // Deep-link from "My Recos": scroll the deck to one specific song card. The
  // reco may live further down the feed, so page forward until it is mounted
  // (and give up cleanly when the feed is exhausted, rather than leaving a
  // stale signal that would hijack a later visit).
  const landOnRecoId = useUiSignals((s) => s.landOnRecoId);
  useEffect(() => {
    if (landOnRecoId == null) return;
    const card = deckRef.current?.querySelector(`[data-testid="discover-card-${landOnRecoId}"]`);
    // feed-position-and-pause — wait for the FIRST page of a circle not cached
    // yet, instead of reading its still-undefined `hasNextPage` as "exhausted".
    const step = landingStep({
      found: card != null,
      loaded: data != null,
      failed: isError,
      hasNextPage,
      isFetching,
    });
    if (step === 'land') {
      card?.scrollIntoView({ block: 'start' });
      useUiSignals.getState().setLandOnReco(null);
    } else if (step === 'fetch') {
      void fetchNextPage();
    } else if (step === 'give-up') {
      useUiSignals.getState().setLandOnReco(null);
    }
  }, [landOnRecoId, feed, data, isError, hasNextPage, isFetching, fetchNextPage]);

  /**
   * reco-comment-replies v2 — open the conversation once its card exists.
   * Consumed like `landOnRecoId` above: a signal left armed would reopen the
   * sheet on every later render.
   */
  const openThreadForRecoId = useUiSignals((s) => s.openThreadForRecoId);
  useEffect(() => {
    if (openThreadForRecoId == null) return;
    if (!feed.some((r) => r.id === openThreadForRecoId)) return;
    useUiSignals.getState().setOpenThreadForReco(null);
  }, [openThreadForRecoId, feed]);

  // Autoplay is enabled after the first swipe, so the first track stays silent.
  const [autoPlay, setAutoPlay] = useState(false);

  // ios-tab-bar — on iOS Home is kept alive between visits: hiding it resets
  // what each visit used to start from (a fresh mount on Android and web).
  useOnHide(() => {
    setAutoPlay(false);
    seenSentRef.current.clear();
  });

  /**
   * feed-position-and-pause — each circle finds its card again. The deck is ONE
   * scroll container: switching to a circle whose feed is cached kept the
   * previous circle's scrollTop. On a circle change (not on mount: `picked` is
   * null there, and a My Recos landing arrives through the mount), go to the
   * top, then — unless a landing is already armed, which wins — ask the
   * existing `landOnRecoId` paging to bring back the remembered card. If that
   * card is gone, the signal gives up and the deck stays at the top.
   */
  const circleSeenRef = useRef<number | null>(null);
  useEffect(() => {
    const previous = circleSeenRef.current;
    circleSeenRef.current = activeCircleId;
    if (previous == null || previous === activeCircleId) return;
    const signals = useUiSignals.getState();
    if (signals.landOnRecoId != null) return;
    deckRef.current?.scrollTo({ top: 0 });
    const remembered = signals.deckPositionFor(activeCircleId);
    if (remembered != null) signals.setLandOnReco(remembered);
  }, [activeCircleId]);

  // Double-tap Découvre → scroll the deck back to the most recent card.
  useOnNewSignal(scrollSignal, () => deckRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));

  return (
    <div className="tw:flex tw:h-full tw:flex-col tw:overflow-hidden" data-testid="screen-home">
      {/* Header — un cran plus bas sur un cercle privé : le logo Racore porte
          son propre blanc, un emoji + un titre non, et chaque pixel pris ici
          est pris à la pochette plus bas. */}
      <header
        className={cn(
          'tw:flex tw:items-center tw:justify-between tw:px-5 tw:pb-2',
          isRacoreCircle ? 'tw:pt-6' : 'tw:pt-4',
        )}
      >
        <div className="tw:flex tw:items-center tw:gap-3.5">
          {isRacoreCircle ? (
            <>
              <RacoreText width={120} height={25} />
              {/* Env marker (QA/DEV builds only) — visible on every circle so testers always know which app they're in. */}
              <EnvLabel />
            </>
          ) : (
            <div className="tw:flex tw:items-center tw:gap-2">
              {activeCircle?.emoji && <span className="tw:text-[22px]">{activeCircle.emoji}</span>}
              <h1
                className="tw:max-w-[190px] tw:truncate tw:heading-3 tw:text-white"
                data-testid="home-circle-title"
              >
                {activeCircle?.name}
              </h1>
            </div>
          )}
          {/* The gear (circle management) is hidden on the official Racore circle. */}
          {!isRacoreCircle && (
            <button
              type="button"
              aria-label="Settings"
              data-testid="btn-home-settings"
              onClick={() => setManageId(activeCircleId)}
              className="tw:relative tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:btn-circle tw:text-white"
            >
              <Settings size={18} />
              {/* Red dot: admin has pending join requests to review (#2). */}
              {shouldShowPendingDot(activeCircle?.membership) && (
                <span
                  data-testid="gear-pending-dot"
                  className="tw:absolute tw:-right-0.5 tw:-top-0.5 tw:size-2.5 tw:rounded-full tw:bg-error tw:ring-2 tw:ring-dark-primary"
                />
              )}
            </button>
          )}
        </div>
        {/* discover-chrome-density — le filtre s'ouvre d'ici, sur la ligne du
            titre. Sa rangée dédiée coûtait 38 pt à une carte qui débordait. */}
        <StyleFilterTrigger
          count={styles.length}
          onOpen={() => setFilterOpen(true)}
          label={t('feedFilter.trigger')}
        />
        {/* Points badge — hidden for now (star + score), will be used later.
        <div className="tw:flex tw:items-center tw:gap-1.5 tw:rounded-full tw:bg-dark-1 tw:py-1 tw:pl-1 tw:pr-3">
          <span className="tw:flex tw:size-6 tw:items-center tw:justify-center tw:rounded-full tw:bg-black/40">
            <Star size={14} className="tw:text-yellow-primary" fill="currentColor" />
          </span>
          <span className="tw:text-[13px] tw:font-semibold tw:text-white">0</span>
        </div>
        */}
      </header>

      {/* Circle selector */}
      <div className="tw:flex tw:gap-4 tw:overflow-x-auto tw:px-5 tw:py-4">
        <CircleItem
          label={t('groups.newCircle')}
          onClick={() => setNewSheet('choose')}
          testid="home-circle-new"
        >
          <span className="tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-2xl tw:bg-dark-1 tw:text-white">
            <Plus size={24} />
          </span>
        </CircleItem>

        {circles.map((c) => {
          const active = activeCircleId === c.id;
          const isOfficial = c.slug === RACORE_OFFICIAL_SLUG;
          const label = isOfficial ? 'Racore' : c.name;
          return (
            <CircleItem
              key={c.id}
              label={label}
              active={active}
              onClick={() => setPicked(c.id)}
              testid={`home-circle-${c.id}`}
            >
              <span className="tw:relative tw:inline-flex">
                <span
                  className={cn(
                    'tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-2xl tw:bg-dark-1',
                    c.emoji ? 'tw:text-[26px]' : 'tw:text-[20px] tw:font-bold tw:text-white',
                    active && 'tw:border-2 tw:border-yellow-primary',
                  )}
                >
                  {c.emoji || label.charAt(0).toUpperCase()}
                </span>
                {/* A9 — unseen new sounds badge; hidden on the circle being viewed.
                    Same `error` token as every other notification marker (the tab
                    dot, the pending-requests dot) — yellow read as a highlight,
                    not as "there is something new for you". */}
                {!active && unseenBadgeLabel(c.membership) != null && (
                  <span
                    data-testid={`badge-unseen-${c.id}`}
                    className="tw:absolute tw:-right-1.5 tw:-top-1.5 tw:flex tw:h-5 tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:bg-error tw:px-1 tw:text-[11px] tw:font-bold tw:text-white"
                  >
                    {unseenBadgeLabel(c.membership)}
                  </span>
                )}
              </span>
            </CircleItem>
          );
        })}
      </div>

      {/* Deck */}
      {isLoadingFeed ? (
        <CenterState>
          <span className="tw:size-7 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
        </CenterState>
      ) : hiddenAll ? (
        <FilterHiddenAllState
          title={t('feedFilter.hiddenAllTitle', { count: hiddenCount })}
          cta={t('feedFilter.hiddenAllCta')}
          // filter-sheet-cleanup — clearing here is a decision too: push it, or
          // the account keeps the old filter and the next sync brings it back.
          onClear={() => {
            clearStyles();
            commitStyles();
          }}
          {...handlers}
        >
          <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />
        </FilterHiddenAllState>
      ) : isError || feed.length === 0 ? (
        // D1 — an empty circle must still be refreshable: there is no deck to
        // pull on here, so the empty state itself carries the gesture.
        <CenterState data-testid="discover-empty" {...handlers}>
          <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />
          <p className="tw:max-w-[260px] tw:text-center tw:text-base tw:text-grey-2">
            {t('discover.noRecommendations')}
          </p>
        </CenterState>
      ) : (
        <div
          ref={deckRef}
          className="tw:relative tw:flex-1 tw:snap-y tw:snap-mandatory tw:overflow-y-auto"
          {...handlers}
          onScroll={(e) => {
            if (!autoPlay) setAutoPlay(true);
            const el = e.currentTarget;
            // feed-position-and-pause — the deck snaps full-height cards, so the
            // card on screen is the rounded page index.
            const onScreen = feed[Math.round(el.scrollTop / Math.max(1, el.clientHeight))];
            if (onScreen) useUiSignals.getState().rememberDeckPosition(activeCircleId, onScreen.id);
            if (hasNextPage && el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
              void fetchNextPage();
            }
          }}
        >
          <PullToRefreshSpinner pullY={pullY} refreshing={refreshing} />
          {feed.map((d) => (
            <DiscoverCard
              key={d.id}
              discovery={d}
              // comment-circle-scope — le cercle de lecture descend d'ici. Il
              // était déjà calculé (`isRacoreCircle` couvre le cercle officiel
              // réel ET le `PUBLIC_CIRCLE_ID` synthétique) et n'était jamais
              // transmis : la carte le devinait, et se trompait.
              circleId={isRacoreCircle ? null : activeCircleId}
              autoPlay={autoPlay}
              openLabel={t('discover.actions.open')}
              recoLabel={t('discover.actions.reco')}
            />
          ))}
        </div>
      )}

      {/* Nouveau → choose: create a circle or join one with a code */}
      <BottomSheet
        open={newSheet === 'choose'}
        title={t('groups.newCircle')}
        onClose={() => setNewSheet(null)}
        testid="new-circle-chooser"
      >
        <div className="tw:flex tw:flex-col tw:gap-3 tw:px-1 tw:pb-2">
          <button
            type="button"
            onClick={() => setNewSheet('create')}
            data-testid="btn-choose-create"
            className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-4 tw:text-left tw:text-white tw:active:opacity-80"
          >
            <span className="tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary/25 tw:text-blue-primary">
              <Plus size={22} />
            </span>
            <span className="tw:text-[15px] tw:font-semibold">{t('groups.chooser.create')}</span>
          </button>
          <button
            type="button"
            onClick={() => setNewSheet('join')}
            data-testid="btn-choose-join"
            className="tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-4 tw:text-left tw:text-white tw:active:opacity-80"
          >
            <span className="tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary/25 tw:text-[20px]">
              🦊
            </span>
            <span className="tw:text-[15px] tw:font-semibold">{t('groups.chooser.join')}</span>
          </button>
        </div>
      </BottomSheet>

      <CreateCircleSheet
        open={newSheet === 'create'}
        onClose={() => setNewSheet(null)}
        onCreated={(g) => {
          setPicked(g.id);
          setShareGroup(g);
        }}
      />

      {shareGroup && (
        <InviteLinkSheet
          open
          groupId={shareGroup.id}
          groupName={shareGroup.name}
          onClose={() => setShareGroup(null)}
        />
      )}

      <JoinCircleSheet open={newSheet === 'join'} onClose={() => setNewSheet(null)} />

      <BottomSheet
        open={filterOpen}
        title={t('feedFilter.title')}
        // On the title row, whose height is reserved: appearing with the first
        // selected style must not shove the sheet down by a row.
        titleAction={
          styles.length > 0 ? (
            <StyleFilterClearButton onClear={clearStyles} label={t('feedFilter.clearAll')} />
          ) : null
        }
        onClose={closeFilterSheet}
      >
        <StyleFilterBar
          selected={styles}
          onToggle={toggleStyle}
          label={(key) => t(`styles.${key}`)}
        />
      </BottomSheet>

      {manageId != null && (
        <ManageCircleScreen groupId={manageId} onClose={() => setManageId(null)} />
      )}
    </div>
  );
}

/**
 * Centred filler for the deck area (loading / empty / error).
 *
 * Forwards the remaining div props so the empty state can carry the
 * pull-to-refresh handlers (D1), and is `relative` so the spinner anchors to its
 * top edge like it does on the deck.
 */
function CenterState({ children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="tw:relative tw:flex tw:flex-1 tw:items-center tw:justify-center tw:px-6 tw:pb-[var(--tab-bar-inset,0px)]"
      {...rest}
    >
      {children}
    </div>
  );
}

interface CircleItemProps {
  label: string;
  active?: boolean;
  onClick: () => void;
  testid?: string;
  children: React.ReactNode;
}

function CircleItem({ label, active, onClick, testid, children }: CircleItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="tw:flex tw:shrink-0 tw:flex-col tw:items-center tw:gap-1"
    >
      {children}
      <span
        className={cn(
          'tw:max-w-[64px] tw:truncate tw:text-[11px]',
          active ? 'tw:font-semibold tw:text-white' : 'tw:text-grey-3',
        )}
      >
        {label}
      </span>
    </button>
  );
}

function DiscoverCard({
  discovery,
  circleId,
  autoPlay,
  openLabel,
  recoLabel,
}: {
  discovery: DiscoverResponse;
  /**
   * comment-circle-scope — le cercle DANS LEQUEL cette carte est lue, `null` sur
   * Racore Officiel. La carte ne le déduit plus des cercles de la reco : ce
   * proxy désignait n'importe lequel d'entre eux, y compris un cercle dont le
   * lecteur n'est pas membre, et faisait apparaître la discussion sur Racore.
   */
  circleId: number | null;
  autoPlay: boolean;
  openLabel: string;
  recoLabel: string;
}) {
  const { t, language } = useTranslation();
  const image = getTrackImageUrl(discovery.track);
  const artists = discovery.track.artists?.map((a) => a.name).join(', ');
  // G-tr — show the reader's language; the "…" menu can flip to the original.
  const commentObj = pickCaption(discovery.comments);
  const commentDisplay = useMemo(
    () => (commentObj ? pickCommentDisplay(commentObj, language) : null),
    [commentObj, language],
  );
  const [showOriginalComment, setShowOriginalComment] = useState(false);
  const comment =
    commentDisplay && !showOriginalComment ? commentDisplay.text : (commentObj?.content ?? null);
  const sender = discovery.sender;

  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const preview = getPreviewUrl(discovery.track);
  const audioId = `discover-${discovery.id}`;
  const { currentId, isPlaying, toggle, play } = useAudioPlayer();
  const playing = currentId === audioId && isPlaying;
  const refresh = () => updateTrackWithDeezer(discovery.track.isrc);

  const { entries, userReaction, react } = useReactions(discovery);
  const [pickerOpen, setPickerOpen] = useState(false);
  // reco-sheet-per-gesture — one gesture, one sheet: the legend opens the
  // legend, the pill the reactions, the 💬 CTA the conversation. Never one sheet
  // with tabs: that coupling is what printed the legend twice in the thread.
  const [sheet, setSheet] = useState<'caption' | 'reactions' | 'discussion' | null>(null);

  // comment-circle-scope — le fil appartient à la paire (reco, cercle). Le hook
  // le jette dès que le cercle change : sans ça, `key={d.id}` faisait réutiliser
  // la même instance d'un onglet à l'autre et les réponses du cercle #1
  // s'affichaient dans le cercle #2.
  const { thread, canReply, load: loadThread, reply } = useCircleThread(discovery.id, circleId);

  const openDiscussion = () => {
    setSheet('discussion');
    // Only the conversation needs the thread: the pill used to load it for
    // nothing. Sur Racore Officiel il n'y a pas de conversation : le hook s'abstient.
    loadThread();
  };

  // Autoplay this card's preview once it becomes the visible (snapped) card —
  // but only after the first swipe (autoPlay), so the initial track stays silent.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !autoPlay || !preview) return;
    // F4 — settle delay. A fast flick crosses the 0.7 threshold on several cards
    // in a row; without this, each of them claimed the player and the deck ended
    // up fighting itself (audible stutter, `isPlaying` out of sync with what you
    // hear). Only a card that is STILL the snapped one after the delay starts
    // playback, and leaving the threshold cancels the pending claim.
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const cancelSettle = () => {
      if (settleTimer !== null) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        cancelSettle();
        if (!entry.isIntersecting || entry.intersectionRatio < 0.7) return;
        settleTimer = setTimeout(() => {
          settleTimer = null;
          play(preview, audioId, refresh, {
            title: discovery.track.title,
            artist: artists,
            artwork: image,
          });
        }, AUTOPLAY_SETTLE_MS);
      },
      { threshold: [0.7] },
    );
    observer.observe(el);
    return () => {
      cancelSettle();
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, preview, audioId]);

  // Record a view the first time this card is meaningfully on screen (analytics).
  const viewedRef = useRef(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6 && !viewedRef.current) {
          viewedRef.current = true;
          void trackRecommendationViewing(discovery.id);
        }
      },
      { threshold: [0.6] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [discovery.id]);

  const favorite = useAuthStore((s) => s.userInfo.favorite_streaming_service) || 'spotify';
  const meId = useAuthStore((s) => s.userInfo.id);
  const openInService = () => {
    void trackTrackOpening(discovery.id, favorite);
    // F2 — Deezer opens the app first on the native shell, web otherwise.
    openServiceUrl(
      favorite,
      getServiceOpenUrl(favorite, `${discovery.track.title} ${artists ?? ''}`),
    );
  };

  // Re-recommend this track, linking the new reco to this one (parent).
  const handleReco = () => {
    useRecoDraft
      .getState()
      .setDraft(mapDiscoverTrackToTrack(discovery.track), 'deezer', discovery.id);
    navigate({ to: RECO_SHARE_ROUTE });
  };

  return (
    <section
      ref={sectionRef}
      // ios-tab-bar — the card runs under the floating iOS bar (seen through the
      // glass); its own content stays above it. 1rem off iOS, as before.
      className="tw:relative tw:flex tw:h-full tw:snap-start tw:flex-col tw:justify-end tw:overflow-hidden tw:px-4 tw:pb-[calc(1rem+var(--tab-bar-inset,0px))]"
      data-testid={`discover-card-${discovery.id}`}
    >
      <div
        aria-hidden="true"
        className="tw:absolute tw:inset-0"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 20%, var(--tw-color-dark-1) 0%, var(--tw-color-dark-primary) 70%)',
        }}
      />
      {image && (
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="tw:absolute tw:inset-0 tw:size-full tw:object-cover tw:opacity-20 tw:blur-1xl"
        />
      )}

      {/* Users' reactions floating over the song background, sized by count */}
      <FloatingReactions entries={entries} />

      <DiscoverCardMenu
        discovery={discovery}
        commentTranslated={commentDisplay?.isTranslated ?? false}
        showingOriginal={showOriginalComment}
        onToggleOriginal={() => setShowOriginalComment((v) => !v)}
      />

      {/* discover-card-polish — le bloc du son. Le panneau translucide est borné
          (voir TrackPanel) : livré pleine largeur il floutait les réactions
          flottantes, alors qu'il existe pour rendre titre et artiste lisibles
          pendant qu'elles volent autour. */}
      <TrackPanel
        image={image}
        title={discovery.track.title}
        artists={artists}
        play={
          <RoundPlayButton
            audioId={audioId}
            size={44}
            playing={playing}
            disabled={!preview}
            onToggle={() =>
              toggle(preview, audioId, refresh, {
                title: discovery.track.title,
                artist: artists,
                artwork: image,
              })
            }
            testId={`btn-discover-play-${discovery.id}`}
            className="tw:absolute tw:bottom-2 tw:right-2"
          />
        }
      />

      {/* Comment card — centered (matches the app) */}
      <div className="tw:relative tw:z-10 tw:mb-4 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-2xl tw:border tw:border-white/10 tw:bg-dark-1/60 tw:p-4 tw:text-center tw:backdrop-blur-md">
        {/* Top reaction badge — pinned top-right. Shows the top emojis aligned
            with a "…" when there are more; tap to see every reaction + count. */}
        {entries.length > 0 && (
          <button
            type="button"
            onClick={() => setSheet('reactions')}
            data-testid="reaction-badge"
            aria-label="See all reactions"
            className="tw:absolute tw:-top-3 tw:right-3 tw:z-10 tw:flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-dark-1 tw:px-2.5 tw:py-1 tw:text-[13px] tw:font-semibold tw:text-white tw:shadow-lg tw:ring-1 tw:ring-white/10"
          >
            <span className="tw:flex tw:items-center">
              {entries.slice(0, 3).map(([emoji]) => (
                <span key={emoji} className="tw:-ml-0.5 tw:first:ml-0">
                  {emoji}
                </span>
              ))}
            </span>
            {entries.length > 3 ? (
              <MoreHorizontal size={16} className="tw:text-grey-3" />
            ) : (
              <span>{entries.reduce((sum, [, n]) => sum + n, 0)}</span>
            )}
          </button>
        )}

        <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5">
          {sender.avatar_url ? (
            <img
              src={sender.avatar_url}
              alt=""
              className="tw:size-10 tw:rounded-full tw:object-cover"
            />
          ) : (
            <span className="tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-[13px] tw:font-semibold tw:text-white">
              {sender.username?.slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className="tw:text-[15px] tw:font-semibold tw:text-white">{sender.username}</span>
        </div>
        {/* caption-opens-discussion — in a private circle, tapping the legend
            opens the conversation (legend on top, "Répondre…"); on Racore
            Official, where replying is refused, the legend sheet shows the full
            text the card truncates. */}
        {comment && (
          <button
            type="button"
            onClick={() =>
              captionSheetFor(canReply) === 'discussion' ? openDiscussion() : setSheet('caption')
            }
            data-testid="btn-discover-comment"
            className="tw:line-clamp-2 tw:text-left tw:text-base tw:text-grey-2"
          >
            {comment}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="tw:relative tw:z-10 tw:flex tw:items-center tw:gap-3">
        <div className="tw:flex-1">
          <ButtonSystem
            variant="secondary"
            label={recoLabel}
            leftIcon={<span className="tw:text-[18px]">🚀</span>}
            onClick={handleReco}
            data-testid="btn-discover-reco"
          />
        </div>
        <div className="tw:flex-1">
          <ButtonSystem
            variant="secondary"
            label={openLabel}
            leftIcon={<ServiceLogo service={favorite} size={18} />}
            onClick={openInService}
            data-testid="btn-discover-open"
          />
        </div>
        <button
          type="button"
          aria-label="React"
          onClick={() => setPickerOpen(true)}
          data-testid="btn-discover-react"
          className={cn(
            // Figma Button/Circle, as tall as Reco / Open: a default ButtonSystem is
            // h-14 + a 1px border each side = 58px. Explicit, because stretch +
            // aspect-square let the width follow the icon (a 24 × 58 pill).
            'tw:flex tw:size-[58px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:btn-circle',
            userReaction ? 'tw:text-[22px]' : 'tw:text-white',
          )}
        >
          {userReaction ? <span>{userReaction}</span> : <SmilePlus size={22} />}
        </button>
        {/* discover-chrome-density — la discussion s'ouvre d'ici, à côté du
            bouton de réaction (qui n'est pas une réaction : c'est ce qui ouvre
            leur choix). Absent sur Racore Officiel, où répondre est refusé par
            le backend : un CTA grisé promettrait ce qu'on refuse ensuite. */}
        {canReply && (
          <DiscussionCount
            count={discovery.commentCount ?? 0}
            onOpen={openDiscussion}
            label={t('discover.actions.discussion')}
          />
        )}
      </div>

      {pickerOpen && (
        <ReactionPicker
          current={userReaction}
          onPick={(emoji) => {
            react(emoji);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <ReactionsSheet
        open={sheet === 'reactions'}
        title={t('discover.reactions.title')}
        entries={entries}
        users={reactionUserEntries(discovery.recommendations_to_users)}
        meId={meId}
        onClose={() => setSheet(null)}
      />

      {comment && (
        <CaptionSheet
          open={sheet === 'caption'}
          author={sender}
          text={comment}
          onClose={() => setSheet(null)}
        />
      )}

      {canReply && (
        <DiscussionSheet
          open={sheet === 'discussion'}
          // The card's legend wins: an edit rewrites the feed cache, not the
          // thread already loaded in this card.
          thread={syncCaption(thread, commentObj) ?? []}
          canReply={canReply}
          onSend={reply}
          onClose={() => setSheet(null)}
        />
      )}
    </section>
  );
}
